<template>
  <div class="chat-container">
    <!-- Bouton de déclenchement du chat -->
    <button title="assistance"
      v-if="!isOpen"
      @click="toggleChat"
      class="chat-trigger-btn flex items-center gap-2 rounded-lg"
      aria-label="Ouvrir le chat"
    >
      <MessageSquareIcon class="icon" /> <p>Besoin d'aide ?</p>
    </button>

    <!-- Fenêtre de chat principale -->
    <div v-if="isOpen" class="chat-window">
      <!-- En-tête du chat -->
      <div class="chat-header">
        <div class="flex items-center gap-2">
          <div class="avatar">
            <img :src="logo" loading="lazy" alt="logo cassuss" class="avatar-icon" />
            <!-- <UserIcon class="avatar-icon" /> -->
          </div>
          <div>
            <h2 class="chat-title">Cassuss Support</h2>
            <p class="chat-status">En ligne</p>
          </div>
        </div>
        <button @click="toggleChat" class="close-btn" aria-label="Fermer le chat">
          <XIcon class="icon" />
        </button>
      </div>

      <!-- Corps du chat -->
      <div ref="chatBody" class="chat-body">
        <!-- Message de bienvenue -->
        <div class="message-bubble message-incoming">
          <div class="message-content">
            <div class="flex items-center gap-2 mb-2">
              <div class="message-avatar">
                 <img :src="logo" loading="lazy" alt="logo cassuss" class="avatar-icon" />
              </div>
              <span class="message-sender">Cassuss Support</span>
            </div>
            <p>👋 Bonjour !</p>
            <p>Que puis-je faire pour vous ?</p>
          </div>
          <span class="message-time">{{ getCurrentTime() }}</span>
        </div>

        <!-- Messages utilisateur -->
        <div 
          v-for="(message, index) in userMessages" 
          :key="'user-' + index"
          class="message-bubble message-outgoing"
        >
          <div class="message-content">
            <p>{{ message.text }}</p>
          </div>
          <span class="message-time">{{ message.time }}</span>
        </div>

        <!-- Messages système -->
        <div 
          v-for="(message, index) in systemMessages" 
          :key="'system-' + index"
          class="message-bubble message-incoming"
        >
          <div class="message-content">
            <div class="flex items-center gap-2 mb-2">
              <div class="message-avatar">
                  <img :src="logo" loading="lazy" alt="logo cassuss" class="avatar-icon" />
              </div>
              <span class="message-sender">Cassuss Support</span>
            </div>
            <p>{{ message.text }}</p>
            
            <!-- Afficher les boutons de catégories si nécessaire -->
            <div v-if="showCategories" class="categories-container mt-3">
              <p class="mb-2 text-sm font-medium">Sélectionnez une catégorie :</p>
              <div class="categories-grid">
                <button
                  v-for="category in categories"
                  :key="category.id"
                  @click="selectCategory(category)"
                  class="category-btn"
                >
                  {{ category.name }}
                </button>
              </div>
            </div>
          </div>
          <span class="message-time">{{ message.time }}</span>
        </div>

        <!-- Boutons de réponse rapide -->
        <div v-if="showQuickReplies" class="quick-replies-container">
          <div class="quick-replies">
            <button
              v-for="(reply, index) in quickReplies"
              :key="index"
              @click="handleQuickReply(reply.action)"
              class="quick-reply-btn"
              :class="{ 'agent-btn': reply.action === 'agent' }"
            >
              <span v-if="reply.icon" class="mr-2">{{ reply.icon }}</span>
              {{ reply.text }}
            </button>
          </div>
        </div>

        <!-- Bouton WhatsApp -->
        <div v-if="showWhatsAppBtn" class="whatsapp-container">
          <button @click="openWhatsApp" class="whatsapp-btn">
            <MessageCircleIcon class="icon mr-2" />
            Discuter sur WhatsApp
          </button>
        </div>

        <!-- Formulaire vendeur -->
        <div v-if="showSellerForm" class="seller-form-container">
          <div class="form-card">
            <h3 class="form-title">Formulaire vendeur</h3>
            <p class="form-description">Remplissez ce formulaire pour vendre sur Cassuss</p>
            
            <form @submit.prevent="submitSellerForm" class="seller-form">
              <div class="form-group">
                <label for="shopName" class="form-label">Nom de la boutique *</label>
                <input
                  v-model="sellerForm.shopName"
                  type="text"
                  id="shopName"
                  required
                  class="form-input"
                  placeholder="Ex: Mode Africaine Elegance"
                />
              </div>
              
              <div class="form-group">
                <label for="managerName" class="form-label">Nom du responsable *</label>
                <input
                  v-model="sellerForm.managerName"
                  type="text"
                  id="managerName"
                  required
                  class="form-input"
                  placeholder="Ex: Jean Dupont"
                />
              </div>
              
              <div class="form-group">
                <label for="address" class="form-label">Adresse / Ville *</label>
                <input
                  v-model="sellerForm.address"
                  type="text"
                  id="address"
                  required
                  class="form-input"
                  placeholder="Ex: Douala, Cameroun"
                />
              </div>
              
              <div class="form-group">
                <label for="phone" class="form-label">Numéro de téléphone *</label>
                <input
                  v-model="sellerForm.phone"
                  type="tel"
                  id="phone"
                  required
                  class="form-input"
                  placeholder="Ex: +237 6XX XXX XXX"
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Catégorie sélectionnée</label>
                <div class="selected-category">
                  <span class="category-tag">{{ sellerForm.category }}</span>
                </div>
              </div>
              
              <div class="form-group">
                <label for="additionalInfo" class="form-label">Informations supplémentaires</label>
                <textarea
                  v-model="sellerForm.additionalInfo"
                  id="additionalInfo"
                  rows="3"
                  class="form-textarea"
                  placeholder="Décrivez vos produits, expérience, etc."
                ></textarea>
              </div>
              
              <button type="submit" class="submit-btn" :disabled="isSubmitting">
                <SendIcon class="icon mr-2" />
                <span v-if="!isSubmitting">📩 Envoyer la demande</span>
                <span v-else>Envoi en cours...</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  MessageSquareIcon, 
  XIcon, 
  UserIcon,
  MessageCircleIcon,
  SendIcon 
} from 'lucide-vue-next';

export default {
  name: 'EcommerceChat',
  
  components: {
    MessageSquareIcon,
    XIcon,
    UserIcon,
    MessageCircleIcon,
    SendIcon
  },
  
  data() {
    return {
          logo: require('@/assets/favicon.jpg'),
      isOpen: false,
      chatStep: 'initial',
      userMessages: [],
      systemMessages: [],
      showQuickReplies: true,
      showCategories: false,
      showWhatsAppBtn: false,
      showSellerForm: false,
      isSubmitting: false,
      
      // Boutons de réponse rapide
      quickReplies: [
        { text: '📍 Vous êtes situé où ?', icon: '📍', action: 'location' },
        { text: '🛒 Je veux vendre mes articles sur votre site', icon: '🛒', action: 'sell' },
        { text: '💬 Discuter avec un agent', icon: '💬', action: 'agent' }
      ],
      
      // Catégories de produits
      
      
      // Formulaire vendeur
      sellerForm: {
        shopName: '',
        managerName: '',
        address: '',
        phone: '',
        category: '',
        additionalInfo: ''
      }
    };
  },
  
  mounted() {
    // Initialiser le chat
    this.resetChat();
  },
  computed:{
    categories(){
        return this.$store.state.categories||[];
    }
  },
  
  watch: {
    // Scroll automatique vers le bas quand de nouveaux messages sont ajoutés
    userMessages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    },
    systemMessages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    }
  },
  
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.resetChat();
      }
    },
    
    resetChat() {
      this.chatStep = 'initial';
      this.userMessages = [];
      this.systemMessages = [];
      this.showQuickReplies = true;
      this.showCategories = false;
      this.showWhatsAppBtn = false;
      this.showSellerForm = false;
      this.resetSellerForm();
    },
    
    resetSellerForm() {
      this.sellerForm = {
        shopName: '',
        managerName: '',
        address: '',
        phone: '',
        category: '',
        additionalInfo: ''
      };
    },
    
    getCurrentTime() {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    
    scrollToBottom() {
      const chatBody = this.$refs.chatBody;
      if (chatBody) {
        chatBody.scrollTop = chatBody.scrollHeight;
      }
    },
    
    handleQuickReply(action) {
      // Ajouter le message de l'utilisateur
      const userMessage = this.quickReplies.find(reply => reply.action === action);
      if (userMessage) {
        this.userMessages.push({
          text: userMessage.text,
          time: this.getCurrentTime()
        });
      }
      
      // Gérer l'action
      switch (action) {
        case 'location':
          this.handleLocation();
          break;
        case 'sell':
          this.handleSell();
          break;
        case 'agent':
          this.handleAgent();
          break;
      }
      
      // Masquer les boutons de réponse rapide
      this.showQuickReplies = false;
    },
    
    handleLocation() {
      this.chatStep = 'location';
      
      // Réponse automatique pour la localisation
      setTimeout(() => {
        this.systemMessages.push({
          text: '📍 Nous sommes basés à Douala, Cameroun. Nous collaborons avec des vendeurs partout en Afrique.',
          time: this.getCurrentTime()
        });
        
        // Afficher le bouton pour discuter avec un agent
        this.showWhatsAppBtn = true;
      }, 500);
    },
    
    handleSell() {
      this.chatStep = 'sell';
      
      // Demander le type d'articles
      setTimeout(() => {
        this.systemMessages.push({
          text: 'Super 🎉 Quel type d\'articles souhaitez-vous vendre ?',
          time: this.getCurrentTime()
        });
        
        // Afficher les catégories
        this.showCategories = true;
      }, 500);
    },
    
    handleAgent() {
      this.chatStep = 'agent';
      
      // Afficher directement le bouton WhatsApp
      setTimeout(() => {
        this.showWhatsAppBtn = true;
      }, 300);
    },
    
    selectCategory(category) {
      // Ajouter la sélection de l'utilisateur
      this.userMessages.push({
        text: category.name,
        time: this.getCurrentTime()
      });
      
      // Masquer les catégories
      this.showCategories = false;
      
      // Pré-remplir la catégorie dans le formulaire
      this.sellerForm.category = category.name;
      
      // Afficher le formulaire vendeur
      setTimeout(() => {
        this.showSellerForm = true;
        this.scrollToBottom();
      }, 500);
    },
    
    openWhatsApp() {
      const phoneNumber = '+237694118310'; // À remplacer par le numéro réel
      const message = encodeURIComponent('Bonjour, je souhaite discuter avec un agent Cassuss.');
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
      window.open(whatsappUrl, '_blank');
    },
    
    async submitSellerForm() {
      if (this.isSubmitting) return;
      
      // Validation simple
      if (!this.sellerForm.shopName || !this.sellerForm.managerName || 
          !this.sellerForm.address || !this.sellerForm.phone) {
        alert('Veuillez remplir tous les champs obligatoires.');
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        // Simuler l'envoi des données (à remplacer par une vraie API)
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Message de confirmation
        this.systemMessages.push({
          text: '✅ Votre demande a été envoyée avec succès ! Un agent vous contactera dans les 24 heures.',
          time: this.getCurrentTime()
        });
        
        // Réinitialiser le formulaire
        this.showSellerForm = false;
        this.resetSellerForm();
        
        // Afficher à nouveau les options
        setTimeout(() => {
          this.showQuickReplies = true;
        }, 1000);
        
      } catch (error) {
        console.error('Erreur lors de l\'envoi du formulaire:', error);
        alert('Une erreur est survenue. Veuillez réessayer.');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.chat-container {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 1000;
}

.chat-trigger-btn {
  width: auto;
 
    padding: 0.75rem 1.25rem;
  
  background-color: #3B82F6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.5);
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.chat-trigger-btn:hover {
  background-color: #2563EB;
  transform: scale(1.05);
  box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.5);
}

.chat-window {
  width: 24rem;
  max-width: 90vw;
  height: 36rem;
  max-height: 80vh;
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-header {
  background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
  color: white;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon {
  width: 100%;
  height: 100%;
    border-radius: 9999px;
}

.chat-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
}

.chat-status {
  font-size: 0.75rem;
  opacity: 0.9;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.icon {
  width: 1.25rem;
  height: 1.25rem;
}

.chat-body {
  flex: 1;
  padding: 1.25rem;
  overflow-y: auto;
  background-color: #F9FAFB;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.message-bubble {
  max-width: 85%;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-incoming {
  align-self: flex-start;
}

.message-outgoing {
  align-self: flex-end;
}

.message-content {
  padding: 0.75rem 1rem;
  border-radius: 1.125rem;
  position: relative;
}

.message-incoming .message-content {
  background-color: white;
  border: 1px solid #E5E7EB;
  border-bottom-left-radius: 0.25rem;
}

.message-outgoing .message-content {
  background-color: #3B82F6;
  color: white;
  border-bottom-right-radius: 0.25rem;
}

.message-avatar {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background-color: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-sender {
  font-size: 0.75rem;
  font-weight: 600;
  color: #4B5563;
}

.message-time {
  font-size: 0.6875rem;
  color: #9CA3AF;
  margin-top: 0.25rem;
  display: block;
  text-align: right;
}

.quick-replies-container {
  margin-top: 0.5rem;
}

.quick-replies {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.quick-reply-btn {
  padding: 0.875rem 1rem;
  background-color: white;
  border: 1px solid #E5E7EB;
  border-radius: 0.75rem;
  text-align: left;
  font-size: 0.9375rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quick-reply-btn:hover {
  background-color: #F3F4F6;
  border-color: #D1D5DB;
  transform: translateY(-1px);
}

.quick-reply-btn:active {
  transform: translateY(0);
}

.agent-btn {
  background-color: #F0F9FF;
  border-color: #BAE6FD;
  color: #0369A1;
}

.categories-container {
  margin-top: 0.5rem;
}

.categories-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.category-btn {
  padding: 0.75rem 1rem;
  background-color: white;
  border: 1px solid #E5E7EB;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.category-btn:hover {
  background-color: #F3F4F6;
  border-color: #3B82F6;
  color: #3B82F6;
}

.whatsapp-container {
  margin-top: 1rem;
  animation: fadeIn 0.5s ease-out;
}

.whatsapp-btn {
  width: 100%;
  padding: 1rem;
  background-color: #25D366;
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 6px -1px rgba(37, 211, 102, 0.3);
}

.whatsapp-btn:hover {
  background-color: #128C7E;
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(37, 211, 102, 0.4);
}

.whatsapp-btn:active {
  transform: translateY(0);
}

.seller-form-container {
  margin-top: 1rem;
  animation: slideInUp 0.5s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-card {
  background-color: white;
  border-radius: 1rem;
  border: 1px solid #E5E7EB;
  padding: 1.5rem;
}

.form-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.form-description {
  font-size: 0.875rem;
  color: #6B7280;
  margin-bottom: 1.5rem;
}

.seller-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.form-label::after {
  content: '*';
  color: #EF4444;
  margin-left: 0.25rem;
}

.form-label:has(+ .selected-category)::after {
  content: '';
}

.form-input, .form-textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input::placeholder, .form-textarea::placeholder {
  color: #9CA3AF;
}

.selected-category {
  padding: 0.75rem 1rem;
  background-color: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 0.5rem;
}

.category-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: #3B82F6;
  color: white;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}

.form-textarea {
  resize: vertical;
  min-height: 5rem;
}

.submit-btn {
  margin-top: 0.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 640px) {
  .chat-window {
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  
  .chat-container {
    bottom: 1rem;
    right: 1rem;
  }
  
  .chat-trigger-btn {
   width: auto;
 
  }
}
</style>